import styled from 'styled-components'

import { DotsLoader, Layout, Text, Tooltip } from '@loadsmart/miranda-react'
import { type ColorToken } from '@loadsmart/miranda-react/dist/tokens'

import { type ShipmentTrackingStatus } from '../../domain/Shipment'
import { StatusIndicator } from './StatusIndicator'

const CustomBox = styled(Layout.Box)`
  height: 40px;
  min-width: 100px;
  align-items: center;
  justify-content: center;
  display: flex;
  border-radius: 100px;
`

const TooltipMessage = styled.div`
  max-width: 210px;
  text-align: center;
`

type VariantContent = {
  color: ColorToken
  text: string
  tooltip?: string
}
export type TrackingIndicatorProps = {
  readonly status: ShipmentTrackingStatus
  readonly loading?: boolean
}

const variants: Record<ShipmentTrackingStatus, VariantContent> = {
  'not-started': {
    color: 'color-neutral-40',
    text: 'Not started',
    tooltip: 'It will be shown as tracking as soon as we receive the first location update',
  },
  tracking: {
    color: 'color-brand',
    text: 'Tracking',
  },
  'not-tracking': {
    color: 'color-danger-60',
    text: 'Not tracking',
    tooltip: 'Not providing locations',
  },
}

export function TrackingIndicator({ status, loading }: TrackingIndicatorProps) {
  const { color, text, tooltip } = variants[status]

  if (loading) {
    return (
      <CustomBox
        borderColor="color-divider"
        borderWidth="border-thin"
        backgroundColor="color-neutral-10"
      >
        <DotsLoader data-testid="tracking-indicator-loading" size="8px" />
      </CustomBox>
    )
  }

  const element = (
    <CustomBox borderColor="color-divider" borderWidth="border-thin" paddingY="none">
      <Layout.Group gap="spacing-2" align="center">
        <StatusIndicator color={color} />
        <Text variant="body-md-bold" color="color-text-placeholder">
          {text}
        </Text>
      </Layout.Group>
    </CustomBox>
  )

  if (!tooltip) {
    return element
  }

  return <Tooltip message={<TooltipMessage>{tooltip}</TooltipMessage>}>{element}</Tooltip>
}
